/**
 * Upcoming Charges screen — the Operations queue of pending card charges
 * (DNKADM-0145). Backed by `GET /v2/admin/pending-charges` (FORWARD CONTRACT
 * DNKAPI-0279, specced-not-yet-deployed — degrades gracefully to an empty page
 * until it ships). It lists deferred card charges created when a reservation is
 * marked Showed/No-show on a card-on-file service; each runs ~30 min after the
 * reservation ends unless waived or charged early.
 *
 * Mirrors the paged admin-list pattern (`ConfigAuditLog`): loading skeleton /
 * empty / error+retry states, server-driven paging, and server-side filters. The
 * default view is the imminent `pending` charges, soonest first.
 *
 * Filters: a status control (default Pending; Pending / Charged / Cancelled /
 * Failed) plus a "Next 24 hours" quick window (`scheduledFrom=now`/
 * `scheduledTo=now+24h`). Location scope is NOT an in-screen filter — the list is
 * auto-scoped to the app-bar service-location chooser (`useServiceLocation()`,
 * DNKADM-0153): a selected `id` is sent as `?locationId=`; a location switch
 * refetches; while no location is resolved (context loading/empty) the param is
 * omitted and the list is tenant-wide.
 *
 * View state in the URL (DNKADM-0430 P1-B): the status, the "Next 24 hours" window and
 * the page live in the query string under the `na` prefix (`naStatus`, `na24h`,
 * `naPage`, see {@link NEEDS_ACTION_URL_DEFAULTS}), so they never collide with the
 * Transaction ledger's own Payments params on the same page. The filters sit in the
 * shared `ListToolbar`. The list is server paged, so it has no sort control (Phase 1).
 *
 * Row actions apply ONLY to `pending` charges (non-pending rows are read-only
 * history): Adjust amount (major-unit input, converted to minor), Waive (won't
 * run), and Charge now (runs immediately — success → toast + paymentRef, failure →
 * toast + failureCode). Every action refetches; a `409` (the charge already ran or
 * was waived) surfaces a fixed message and refetches. Each row links out to the
 * reservation (`/reservations?reservation=<uuid>`; the calendar consumes the param
 * in DNKADM-0147 — here we just render the link).
 */
import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { IconAction } from '@/components/IconAction';
import { ListToolbar } from '@/components/list/ListToolbar';
import {
  parseOption,
  parsePage,
  toPageParam,
  useListUrlState,
} from '@/components/list/useListUrlState';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { composeCardLabel } from '@/utils/cardBrand';
import { declineReasonLine } from '@/utils/declineReason';
import { scrubBrand } from '@/utils/whitelabel';
import { buildPendingChargesMaps } from '@/theme/maps/pendingCharges';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { ApiError } from '@/api/errors';
import { useServiceLocation } from '@/location/useServiceLocation';
import { useCalendarZone } from '@/location/useCalendarZone';
import { useStaffTier } from '@/auth/authHooks';
import {
  listPendingCharges,
  adjustPendingCharge,
  waiveReservationPendingCharge,
  chargePendingChargeNow,
  PENDING_CHARGE_STATUSES,
  type PendingCharge,
  type PendingChargeStatus,
} from '@/api/pendingCharges';
import { formatMinorMoney, minorUnitFactor } from '@/utils/v2Format';
import { ScreenError } from '@/components/ScreenError';
import { DATE_TIME, formatInstant, formatInstantMs, TIME_ONLY } from '@/utils/instantFormat';

const DEFAULT_PAGE_SIZE = 20;
const SKELETON_ROWS = 6;
/** The status filter always drives a single concrete status (default Pending). */
const DEFAULT_STATUS: PendingChargeStatus = 'pending';
/**
 * The queue's URL params (DNKADM-0430 P1-B), `na`-prefixed because the queue shares the
 * Transaction ledger's Payments tab with the ledger's own `status`/`page`. `naPage` is
 * 1-indexed in the URL; the queue's state is 0-indexed and converts at the edge.
 */
const NEEDS_ACTION_URL_DEFAULTS = {
  naStatus: DEFAULT_STATUS,
  na24h: '',
  naPage: '1',
} as const satisfies Record<string, string>;
/** The `na24h` value for "Next 24 hours" on. */
const NEXT_24H_ON = '1';

/** 24 hours in ms — the "Next 24 hours" quick-window span. */
const DAY_MS = 24 * 60 * 60 * 1000;

/** Human label per known status (unknown statuses render raw). */
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  charged: 'Charged',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

/** Decimal places for a currency's minor unit (factor 1→0, 1000→3, else 2). */
function decimalsFor(currency: string): number {
  const factor = minorUnitFactor(currency);
  return factor === 1 ? 0 : factor === 1000 ? 3 : 2;
}

/** Minor units → a major-unit input string (e.g. 2500 USD → "25.00"). */
function minorToMajorInput(amountMinor: number, currency: string): string {
  return (amountMinor / minorUnitFactor(currency)).toFixed(decimalsFor(currency));
}

/** A major-unit input string → minor units (rounded), or null when not a number. */
function majorToMinor(value: string, currency: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * minorUnitFactor(currency));
}

/** The row amount, or an em dash when the currency is unknown (can't format safely). */
function amountLabel(charge: PendingCharge): string {
  if (charge.currency === '') return '—';
  return formatMinorMoney(charge.amountMinor, charge.currency);
}

/**
 * Format an ISO/epoch instant (e.g. "Jun 10, 9:12 AM"); falls back to the raw
 * string when unparseable and to an em dash when absent. Mirrors ConfigAuditLog.
 */
function formatDateTime(ts: string | null, timeZone: string | null = null): string {
  // DNKADM-0437 V-25: rendered in the VENUE zone like the rest of the console (the device
  // zone only when the location has none, or names a zone the runtime rejects), through the
  // shared instant formatter (DNKADM-0476 F-05).
  return formatInstant(ts, timeZone, DATE_TIME);
}

/** Time-only (e.g. "10:00 AM"); em dash when absent, the raw value when unparseable. */
function formatTimeOnly(ts: string | null, timeZone: string | null = null): string {
  if (ts === null) return '—';
  const ms = Date.parse(ts);
  if (!Number.isFinite(ms)) return ts;
  return formatInstantMs(ms, timeZone, TIME_ONLY);
}

/** "start – endTime" for the reservation window; the start carries the date. */
function formatWindow(start: string | null, end: string | null, timeZone: string | null = null): string {
  if (start === null && end === null) return '—';
  if (start === null) return formatDateTime(end, timeZone);
  if (end === null) return formatDateTime(start, timeZone);
  return `${formatDateTime(start, timeZone)} – ${formatTimeOnly(end, timeZone)}`;
}

/** Humanize a failure code, e.g. `card_declined` → "Card declined". */
function humanizeCode(code: string): string {
  const spaced = code.replace(/[_-]+/g, ' ').trim();
  if (spaced.length === 0) return code;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** The fixed message shown when an action hits a 409 (already ran / waived). */
const CONFLICT_MESSAGE = 'This charge can no longer be changed — it already ran or was waived.';

export function UpcomingChargesScreen() {
  return (
    <Box>
      <Typography variant="h1" component="h1">
        Upcoming charges
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 640 }}>
        Deferred card charges from reservations marked showed or no-show on a card-on-file service.
        Each runs about 30 minutes after the reservation ends unless you waive it or charge it early.
      </Typography>
      <UpcomingChargesQueue />
    </Box>
  );
}

/**
 * The pending-charge queue body — the filters, table, paging, and the
 * adjust/waive/charge dialogs — WITHOUT the page heading. Rendered beneath the
 * standalone Upcoming charges heading above, and reused heading-less as the
 * Transaction Ledger Payments "Needs action" queue (DNKADM-0154 / DNKADM-0377). Reads the app
 * service-location scope via `useServiceLocation()` (DNKADM-0153) and requires a
 * `ToastProvider` ancestor.
 *
 * `emptyLayout` (DNKADM-0378): `'full'` (default) keeps today's icon empty
 * state + filters + table chrome. `'quiet'` is the ledger Payments mount —
 * empty ready is a one-line status; the ~30-minute helper lives here when
 * there are rows so the ledger does not duplicate `useResource`.
 */
export function UpcomingChargesQueue({
  emptyLayout = 'full',
}: {
  emptyLayout?: 'full' | 'quiet';
} = {}) {
  const showToast = useToast();
  // DNKADM-0375: a STAFF session may Charge now, but Adjust amount and Waive stay
  // OWNER/ADMIN on the server (DNKAPI-0532), so those two are absent for it.
  const canEditFee = !useStaffTier();
  // Location scope comes from the app-bar chooser, not an in-screen filter (DNKADM-0153).
  const { selectedId } = useServiceLocation();
  // Venue IANA zone of the selected location, else the tenant's (DNKADM-0437 V-25,
  // DNKADM-0478; same lookup as the ledger).
  const venueTimeZone = useCalendarZone();
  const { values, set } = useListUrlState(NEEDS_ACTION_URL_DEFAULTS);
  const status = parseOption(values.naStatus, PENDING_CHARGE_STATUSES, DEFAULT_STATUS);
  const next24h = values.na24h === NEXT_24H_ON;
  const page = parsePage(values.naPage) - 1;
  const filtersActive = status !== DEFAULT_STATUS || next24h;
  const [adjustTarget, setAdjustTarget] = useState<PendingCharge | null>(null);
  const [waiveTarget, setWaiveTarget] = useState<PendingCharge | null>(null);
  const [chargeTarget, setChargeTarget] = useState<PendingCharge | null>(null);

  const charges = useResource(
    useCallback(() => {
      // Compute the "Next 24 hours" window at fetch time so a refetch re-anchors it.
      const window = next24h
        ? {
            scheduledFrom: new Date().toISOString(),
            scheduledTo: new Date(Date.now() + DAY_MS).toISOString(),
          }
        : {};
      return listPendingCharges({
        status,
        page,
        size: DEFAULT_PAGE_SIZE,
        // Scope to the app service location; omit while none is resolved (tenant-wide).
        ...(selectedId !== null ? { locationId: selectedId } : {}),
        ...window,
      });
    }, [status, selectedId, next24h, page]),
    [status, selectedId, next24h, page],
  );

  const items = charges.data?.items ?? [];
  const totalPages = charges.data?.totalPages ?? 1;

  const refetch = charges.refetch;

  const changeStatus = (next: PendingChargeStatus) => set({ naStatus: next, naPage: null });
  const toggleNext24h = (next: boolean) =>
    set({ na24h: next ? NEXT_24H_ON : null, naPage: null });
  const clearFilters = () => set({ naStatus: null, na24h: null, naPage: null });
  const goToPage = (next: number) => set({ naPage: toPageParam(next + 1) });

  /** Shared action error handling: 409 → fixed message + refetch; else error toast. */
  const handleActionError = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        showToast(CONFLICT_MESSAGE, 'error');
        refetch();
        return;
      }
      showToast('Something went wrong. Please try again.', 'error');
    },
    [showToast, refetch],
  );

  // Returns true on success (the dialog closes), false on failure (the dialog stays
  // open and re-enables its controls so a 409/5xx never freezes it — the admin's
  // typed amount is preserved for a retry or an explicit Cancel).
  const submitAdjust = useCallback(
    async (charge: PendingCharge, amountMinor: number): Promise<boolean> => {
      try {
        await adjustPendingCharge(charge.id, { amountMinor });
        setAdjustTarget(null);
        showToast('Charge amount updated.');
        refetch();
        return true;
      } catch (err) {
        handleActionError(err);
        return false;
      }
    },
    [showToast, refetch, handleActionError],
  );

  // The confirm dialogs carry no user input, and re-confirming a stale action just
  // re-fires the same (already-failing) call — so close the dialog on BOTH the
  // success and the error path (handleActionError still toasts + refetches on 409).
  const submitWaive = useCallback(
    async (charge: PendingCharge) => {
      try {
        await waiveReservationPendingCharge(charge.reservationUuid);
        showToast('Charge waived — it won’t run.');
        refetch();
      } catch (err) {
        handleActionError(err);
      } finally {
        setWaiveTarget(null);
      }
    },
    [showToast, refetch, handleActionError],
  );

  const submitChargeNow = useCallback(
    async (charge: PendingCharge) => {
      try {
        const updated = await chargePendingChargeNow(charge.id);
        if (updated?.status === 'failed') {
          // DNKAPI-0676: a known decline reason says why; OTHER/unknown keep the code line.
          const reasonLine = declineReasonLine(updated.declineReason);
          const reason = updated.failureCode !== null ? ` (${humanizeCode(updated.failureCode)})` : '';
          showToast(reasonLine !== null ? `Charge failed. ${reasonLine}` : `Charge failed${reason}.`, 'error');
        } else {
          // Lead with the money and the person (DNKADM-0303 U6) — a payment
          // ULID means nothing at a front desk. The reference stays for bug
          // reports, demoted to a trailing parenthetical. The returned item is
          // authoritative (the amount may have been adjusted); the row fills
          // any enrichment gap.
          const result = updated ?? charge;
          const amount =
            result.currency !== '' ? ` ${formatMinorMoney(result.amountMinor, result.currency)}` : '';
          const who = scrubBrand(result.contactName ?? charge.contactName);
          const ref = updated?.paymentRef != null ? ` (ref ${updated.paymentRef})` : '';
          showToast(`Charged${amount}${who !== null ? ` — ${who}` : ''}${ref}.`);
        }
        refetch();
      } catch (err) {
        handleActionError(err);
      } finally {
        setChargeTarget(null);
      }
    },
    [showToast, refetch, handleActionError],
  );

  const quiet = emptyLayout === 'quiet';
  const hasRows = charges.status === 'ready' && items.length > 0;
  // The quiet one-liner is for the DEFAULT queue only: default filters on the first page.
  // With a non-default filter (which a link or a reload can now carry) the filters stay
  // visible and an empty result shows the table's empty state, so the operator can see and
  // clear what is narrowing the queue. A page past the first that comes back empty (a stale
  // link) is never "nothing needs action": page 1 may still hold charges.
  const quietEmpty =
    quiet && charges.status === 'ready' && items.length === 0 && !filtersActive && page === 0;
  const showFilters = !quiet || hasRows || filtersActive;

  return (
    <Box>
      {quiet && hasRows && (
        <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 680 }}>
          Card-on-file bookings appear here after check-in. Charge, adjust, or waive before they
          run (~30 minutes).
        </Typography>
      )}

      {showFilters && (
        <Filters
          status={status}
          onStatusChange={changeStatus}
          next24h={next24h}
          onNext24hChange={toggleNext24h}
          onClear={clearFilters}
          canClear={filtersActive}
        />
      )}

      {charges.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={charges.error}
          what="upcoming charges"
          onRetry={refetch}
          retryMessage="Something went wrong while loading the queue."
          sx={{ mt: 2 }}
        />
      ) : quietEmpty ? (
        <Typography variant="body2" role="status" sx={{ mt: 0.5 }}>
          No pending bookings need action.
        </Typography>
      ) : (
        <Paper elevation={1} sx={{ mt: 2.25, overflow: 'hidden' }}>
          <TableContainer>
            <Table aria-label="Upcoming charges" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Reservation</TableCell>
                  <TableCell>Card</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {charges.status === 'loading' &&
                  Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={`sk-${i}`} />)}

                {charges.status === 'ready' && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <EmptyState />
                    </TableCell>
                  </TableRow>
                )}

                {charges.status === 'ready' &&
                  items.map((charge) => (
                    <ChargeRow
                      key={charge.id}
                      charge={charge}
                      timeZone={venueTimeZone}
                      {...(canEditFee
                        ? {
                            onAdjust: () => setAdjustTarget(charge),
                            onWaive: () => setWaiveTarget(charge),
                          }
                        : {})}
                      onChargeNow={() => setChargeTarget(charge)}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Past the first page the pager stays even when the page is empty (a stale
              link), so Previous is the way back. */}
          {charges.status === 'ready' && (items.length > 0 || page > 0) && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPrev={() => goToPage(Math.max(0, page - 1))}
              onNext={() => goToPage(page + 1)}
            />
          )}
        </Paper>
      )}

      {adjustTarget !== null && (
        <AdjustDialog
          charge={adjustTarget}
          onClose={() => setAdjustTarget(null)}
          onSave={(amountMinor) => submitAdjust(adjustTarget, amountMinor)}
        />
      )}

      <ConfirmDialog
        open={waiveTarget !== null}
        title="Waive this charge?"
        body="Waive this charge? It won’t run."
        confirmLabel="Waive charge"
        onCancel={() => setWaiveTarget(null)}
        onConfirm={() => {
          if (waiveTarget !== null) void submitWaive(waiveTarget);
        }}
      />

      <ConfirmDialog
        open={chargeTarget !== null}
        title="Charge this card now?"
        body="Charge this card now? This runs the charge immediately."
        confirmLabel="Charge now"
        onCancel={() => setChargeTarget(null)}
        onConfirm={() => {
          if (chargeTarget !== null) void submitChargeNow(chargeTarget);
        }}
      />
    </Box>
  );
}

function Filters({
  status,
  onStatusChange,
  next24h,
  onNext24hChange,
  onClear,
  canClear,
}: {
  status: PendingChargeStatus;
  onStatusChange: (next: PendingChargeStatus) => void;
  next24h: boolean;
  onNext24hChange: (next: boolean) => void;
  onClear: () => void;
  canClear: boolean;
}) {
  // The status always drives one concrete status (no "All" option), so it is a plain
  // select in the toolbar's filter slot rather than a `ListSelectFilter`.
  return (
    <ListToolbar
      filters={
        <>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) =>
              onStatusChange(parseOption(e.target.value, PENDING_CHARGE_STATUSES, DEFAULT_STATUS))
            }
            sx={{ minWidth: 180 }}
          >
            {PENDING_CHARGE_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Switch checked={next24h} onChange={(e) => onNext24hChange(e.target.checked)} />
            }
            label="Next 24 hours"
          />
        </>
      }
      onClear={onClear}
      canClear={canClear}
    />
  );
}

function StatusChip({ status }: { status: PendingChargeStatus }) {
  const theme = useTheme();
  const maps = buildPendingChargesMaps(theme.palette.mode);
  const label = STATUS_LABELS[status] ?? status;
  const tint = (maps.status as Record<string, { bg: string; fg: string }>)[status] ?? maps.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: tint.bg, color: tint.fg }}
    />
  );
}

function ChargeRow({
  charge,
  timeZone,
  onAdjust,
  onWaive,
  onChargeNow,
}: {
  charge: PendingCharge;
  timeZone: string | null;
  /** Absent for a session that may not edit the fee (STAFF, DNKADM-0375). */
  onAdjust?: () => void;
  onWaive?: () => void;
  onChargeNow: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const contact = charge.contactName ?? charge.contactEmail ?? '—';
  const service = [charge.serviceName, charge.variationName]
    .filter((s): s is string => s !== null)
    .join(' · ');
  // DNKADM-0365: `brand · masked`, EXCEPT when pay-api already embedded the brand in
  // `cardMasked` — then the masked pan alone is the label, so the row cannot read
  // "VISA · VISA **** 1111". The choice is made before the separator goes in; nothing
  // parses or rewrites the mask.
  const card = composeCardLabel(charge.cardBrand, charge.cardMasked, ' · ');
  const isPending = charge.status === 'pending';
  // DNKAPI-0676: on a failed charge, the decline reason's line when known, else the code as before.
  const failureNote =
    charge.status !== 'failed'
      ? null
      : (declineReasonLine(charge.declineReason) ??
        (charge.failureCode !== null ? humanizeCode(charge.failureCode) : null));

  return (
    <TableRow hover>
      <TableCell>{contact}</TableCell>
      <TableCell>
        <Stack sx={{ gap: 0.25 }}>
          <span>{service === '' ? '—' : service}</span>
          <RouterLink
            to={`/reservations?reservation=${encodeURIComponent(charge.reservationUuid)}`}
            style={{ fontSize: 12 }}
          >
            View reservation
          </RouterLink>
        </Stack>
      </TableCell>
      <TableCell>{formatWindow(charge.start, charge.end, timeZone)}</TableCell>
      <TableCell>{card === '' ? '—' : card}</TableCell>
      <TableCell align="right">{amountLabel(charge)}</TableCell>
      <TableCell>{formatDateTime(charge.scheduledAt, timeZone)}</TableCell>
      <TableCell>
        <Stack sx={{ gap: 0.5, alignItems: 'flex-start' }}>
          <StatusChip status={charge.status} />
          {failureNote !== null && (
            <Typography sx={{ fontSize: 11, color: t.error, maxWidth: 260 }}>
              {failureNote}
            </Typography>
          )}
          {charge.status === 'charged' && charge.paymentRef !== null && (
            <Typography sx={{ fontSize: 11, color: t.textSubtle }}>
              {charge.paymentRef}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        {isPending ? (
          <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'flex-end' }}>
            {onAdjust !== undefined && (
              <Button size="small" onClick={onAdjust}>
                Adjust amount
              </Button>
            )}
            {onWaive !== undefined && (
              <Button size="small" color="inherit" onClick={onWaive}>
                Waive
              </Button>
            )}
            <Button size="small" variant="contained" onClick={onChargeNow}>
              Charge now
            </Button>
          </Stack>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/**
 * Adjust-amount dialog — a major-unit amount input pre-filled from the current
 * amount. On save the value is converted to minor units. Validates a finite `>= 0`
 * amount before enabling save.
 */
function AdjustDialog({
  charge,
  onClose,
  onSave,
}: {
  charge: PendingCharge;
  onClose: () => void;
  /** Resolves true when the save succeeded (dialog closes) or false on failure. */
  onSave: (amountMinor: number) => Promise<boolean>;
}) {
  const [value, setValue] = useState(() => minorToMajorInput(charge.amountMinor, charge.currency));
  const [saving, setSaving] = useState(false);

  const minor = majorToMinor(value, charge.currency);
  const invalid = minor === null || minor <= 0;

  const save = async () => {
    if (invalid || minor === null) return;
    setSaving(true);
    const ok = await onSave(minor);
    // On success the dialog unmounts (its target is cleared); on failure re-enable
    // the controls so a 409/5xx never leaves the dialog frozen.
    if (!ok) setSaving(false);
  };

  // Only attach the currency adornment when we have a code (exactOptionalPropertyTypes:
  // never set a present-but-undefined slotProps.input).
  const slotProps =
    charge.currency === ''
      ? undefined
      : {
          input: {
            startAdornment: <InputAdornment position="start">{charge.currency}</InputAdornment>,
          },
        };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust charge amount</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Amount"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={value.trim().length > 0 && invalid}
          helperText={value.trim().length > 0 && invalid ? 'Enter an amount greater than 0.' : ' '}
          {...(slotProps ? { slotProps } : {})}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={invalid || saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>{body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkeletonRow() {
  return (
    <TableRow>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton height={18} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function EmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="payments" sx={{ fontSize: 44, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>No upcoming charges</Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Charges from reservations marked showed or no-show on a card-on-file service will appear here.
      </Typography>
    </Box>
  );
}

function Pagination({
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const atLastPage = page >= totalPages - 1;
  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page + 1}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 0}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}
